import { Injectable } from '@angular/core'
import {
  EventType,
  GlobalErrorEvent,
  GlobalEvent,
  GlobalMessagingService,
  HeliosMessageType,
  SessionEndEvent,
  SessionErrorEvent,
} from '@helios/messaging'
import {
  HeliosWebsocketError,
  inactivateSession,
  removeSession,
  reportError,
  RootState,
  selectActiveSessionIdentifier,
  selectSessions,
} from '@helios/state-management'
import { compareSets, getToday } from '@helios/utils'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators'
import { ErrorInterceptor } from '../interceptors/error.interceptor'
import { v4 as UUID } from 'uuid'

/**
 * Monitors SSE for global events which are not tied to a session
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalEventsService {
  private logging = false
  private readonly unRegister$ = new Subject<void>()

  /** @internal */
  activeSessionId?: string
  /** @internal */
  sessionIds: string[] = []

  private readonly unRegisterCallback = () => {
    this.unRegister$.next()
    this.unRegister$.complete()
  }

  constructor(
    private readonly messagingService: GlobalMessagingService,
    private readonly store: Store<RootState>,
  ) {
    this.store
      .select(selectSessions)
      .pipe(
        distinctUntilChanged((a, b) =>
          compareSets(new Set(a.map(x => x.id)), new Set(b.map(x => x.id))),
        ),
        map(x => x.map(y => y.id)),
      )
      .subscribe(sessionIds => (this.sessionIds = sessionIds))

    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(distinctUntilChanged())
      .subscribe(activeSessionId => (this.activeSessionId = activeSessionId))
  }

  initialize(logging: boolean = false): () => void {
    this.logging = logging
    this.subscribeHeartbeat()
    this.subscribeSessionEnd()
    this.subscribeErrorEvents()
    return this.unRegisterCallback
  }

  /** @internal */
  subscribeSessionEnd() {
    this.messagingService
      .on<unknown, SessionEndEvent>(
        EventType.MESSAGE,
        HeliosMessageType.SessionEnd,
      )
      .pipe(
        takeUntil(this.unRegister$),
        tap(event => {
          if (!this.sessionIds.includes(event.heliosSessionId)) {
            return
          }

          if (this.logging) {
            console.log('session end', event.payload)
          }

          if (this.activeSessionId === event.heliosSessionId) {
            this.store.dispatch(
              inactivateSession({ sessionId: event.heliosSessionId }),
            )
          }

          this.store.dispatch(
            removeSession({ sessionId: event.heliosSessionId }),
          )
        }),
      )
      .subscribe()
  }

  private subscribeHeartbeat() {
    this.messagingService
      .on<unknown, GlobalEvent<unknown>>(
        EventType.MESSAGE,
        HeliosMessageType.GlobalHeartbeat,
      )
      .pipe(
        takeUntil(this.unRegister$),
        tap(event => {
          if (this.logging) {
            console.log(`heartbeat: ${event.timestampUtc}`)
          }
        }),
      )
      .subscribe()
  }

  private subscribeErrorEvents() {
    this.messagingService
      .on<unknown, SessionErrorEvent | GlobalErrorEvent>(EventType.MESSAGE, [
        HeliosMessageType.SessionError,
        HeliosMessageType.GlobalError,
      ])
      .pipe(
        takeUntil(this.unRegister$),
        tap(event => {
          if (this.logging) {
            console.log(`ErrorEvent: ${event.payload}`)
          }
          this.handleStandardApiError(event)
        }),
      )
      .subscribe()
  }

  /**
   * @internal
   * @param eventType
   * @param error
   */
  handleStandardApiError(event: SessionErrorEvent | GlobalErrorEvent) {
    const error = event.payload ?? {}
    const msg = ErrorInterceptor.standardApiErrorToMessage(
      error,
      event.eventType,
    )

    this.store.dispatch(
      reportError({
        id: UUID(),
        timestamp: getToday(),
        message: msg,
        type: 'websocket',
        sessionId: event.heliosSessionId,
        error: JSON.stringify(error),
        subType: event.eventType.valueOf(),
      } as HeliosWebsocketError),
    )
  }
}
